import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { ProcessResponseDTO } from '@/api/generated';
import CreateProcessModalIcon from '@/assets/icons/square-briefcase-color.svg?react';
import CachedLocationDisplay from '@/components/fields/Location/CachedLocationDisplay';
import HeaderProperty from '@/components/HeaderProperty';
import SeparatedList from '@/components/SeparatedList';

interface CreateProcessHeaderProps {
  process?: Omit<ProcessResponseDTO, 'organization' | 'location'> & {
    location?: string;
    organization?: string;
  };
}

const CreateProcessHeader: React.FC<CreateProcessHeaderProps> = ({ process }) => {
  const { t } = useTranslation(['processes', 'settings', 'common']);

  return (
    <Stack direction="row" alignItems="center" sx={{ mx: 5, pt: 2 }}>
      <Box component={CreateProcessModalIcon} sx={{ marginInlineStart: 2 }} />

      <Typography variant="h3" color="text.secondary">
        {process?.name}
      </Typography>

      <SeparatedList
        separator={<Divider orientation="vertical" flexItem sx={{ mx: 3 }} />}
        sx={{ alignItems: 'center', marginInlineStart: 3, '& .HeaderProperty-root': { mt: 0, marginInlineEnd: 0 } }}
      >
        {process?.organization && (
          <HeaderProperty
            variant="body3"
            label={t('layouts.processCreate.location')}
            fallback={t('common:labels.notAvailable')}
            sx={{ alignItems: 'center' }}
          >
            <CachedLocationDisplay organizationSlug={process?.organization} locationSlug={process?.location} outlined />
          </HeaderProperty>
        )}
        {process?.assessmentType && (
          <HeaderProperty variant="body3" label={t('settings:processSettings.form.basicInfo.assessmentType')} skipEmpty>
            {t(`settings:processSettings.form.options.assessmentType.${process?.assessmentType.toLowerCase()}`)}
          </HeaderProperty>
        )}
      </SeparatedList>
    </Stack>
  );
};

export default CreateProcessHeader;
